if (!customElements.get('hercules-collection-card')) {
  customElements.define(
    'hercules-collection-card',
    class HerculesCollectionCard extends HTMLElement {
      static nextModalId = 0;

      constructor() {
        super();
        this.addEventListener('change', (event) => {
          if (event.target.matches('.spf-product__variants')) this.updateVariant();
        });
        this.addEventListener('click', (event) => {
          // The theme's modal-opener has already handled this button; do not also open Globo's popup.
          if (event.target.closest('.open-quick-view')) event.stopPropagation();
        });
        // Handle this card's swatches before Globo's delegated handler can update only part of the card.
        ['click', 'keydown', 'mouseover'].forEach((type) => {
          this.addEventListener(type, this.onSwatch.bind(this), true);
        });
        ['click', 'touchend', 'submit'].forEach((type) => {
          this.addEventListener(type, (event) => {
            if (type !== 'submit' && !event.target.closest('button.spf-product__form-btn-addtocart')) return;
            if (!this.canPurchase()) {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.updateVariant();
            }
          }, true);
        });
      }

      connectedCallback() {
        // Also initializes cards inserted later by collection filters or pagination.
        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.updateVariant();
          const template = this.querySelector('[data-card-quick-view]');
          if (template && !this.quickView) {
            this.quickView = template.content.firstElementChild.cloneNode(true);
            this.quickView.id = `CardQuickView-${++this.constructor.nextModalId}`;
            // Native modals live under body; keep ownership here so filtered-out cards clean up their popup.
            this.quickView.moved = true;
            this.quickView.dataset.section = this.closest('.shopify-section')?.id.replace('shopify-section-', '') || 'collection-card';
            this.querySelector('modal-opener').dataset.modal = `#${this.quickView.id}`;
            document.body.appendChild(this.quickView);
          }
        });
      }

      disconnectedCallback() {
        if (this.quickView?.hasAttribute('open')) this.quickView.hide(true);
        this.quickView?.remove();
        this.quickView = null;
      }

      get select() {
        return this.querySelector('.spf-product__variants');
      }

      get variant() {
        return this.select?.selectedOptions[0];
      }

      isAvailable(variant) {
        return variant?.dataset.available === 'true' || variant?.dataset.available === '1';
      }

      canPurchase() {
        return this.dataset.hidePrice !== 'true' && this.isAvailable(this.variant) && Number(this.variant.dataset.price) > 0;
      }

      setVisible(element, visible) {
        if (!element) return;
        element.hidden = !visible;
        // App styles can override the browser's default [hidden] rule.
        element.style.display = visible ? '' : 'none';
      }

      onSwatch(event) {
        const swatch = event.target.closest('.spf-product__swatch');
        if (!swatch) return;
        if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
        if (event.type === 'mouseover' && (!this.closest('.swatch-hover') || swatch.contains(event.relatedTarget))) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.querySelectorAll('.spf-product__swatch').forEach((item) => {
          if (item.dataset.option === swatch.dataset.option) item.classList.toggle('is-selected', item === swatch);
        });
        const selections = [...this.querySelectorAll('.spf-product__swatch.is-selected')];
        const matches = [...this.select.options].filter((option) =>
          selections.every((item) => option.dataset[`option${item.dataset.option}`] === item.dataset.optionValue)
        );
        const variant = matches.find((option) => this.isAvailable(option)) || matches[0];
        this.select.value = variant?.value || '';
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateVariant() {
        if (!this.select) return;
        const variant = this.variant;
        const data = variant?.dataset || {};
        const canPurchase = this.canPurchase();
        const hidePrice = this.dataset.hidePrice === 'true' || !(Number(data.price) > 0);
        const onSale = Boolean(variant) && !hidePrice && Number(data.compare_at_price) > Number(data.price);
        this.dataset.variantId = variant?.value || '';
        this.select.disabled = !canPurchase;
        this.classList.toggle('spf-soldout', !this.isAvailable(variant));

        const sku = this.querySelector('.part-number');
        sku.textContent = data.sku ? `Part #${data.sku}` : '';
        this.setVisible(sku, Boolean(data.sku));
        const price = this.querySelector('.spf-product-card__price-wrapper');
        price.replaceChildren();
        const addPrice = (className, text) => {
          const span = document.createElement('span');
          span.className = className;
          span.textContent = text;
          price.append(span);
        };
        if (!variant || hidePrice) {
          addPrice('spf-product-card__price', variant ? 'Contact for Price' : 'Unavailable');
        } else {
          if (onSale) addPrice('spf-product-card__oldprice', data.moneyCompare);
          addPrice(onSale ? 'spf-product-card__saleprice' : 'spf-product-card__price', data.moneyPrice);
        }
        this.setVisible(this.querySelector('[data-card-sale]'), onSale);
        this.setVisible(this.querySelector('.stock-label--in'), this.isAvailable(variant));
        this.setVisible(this.querySelector('.stock-label--out'), Boolean(variant) && !this.isAvailable(variant));
        this.setVisible(this.querySelector('quantity-popover'), canPurchase);
        const button = this.querySelector('button.spf-product__form-btn-addtocart');
        button.disabled = !canPurchase;
        this.setVisible(button, canPurchase);
        this.setVisible(this.querySelector('.spf-product__form-btn-contactus'), Boolean(variant) && !canPurchase);

        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.delete('variant');
        if (variant) url.searchParams.set('variant', variant.value);
        this.querySelectorAll('.spf-product-card__title a, .spf-product-card__image-wrapper').forEach((link) => {
          link.href = url.href;
        });
        this.querySelector('.open-quick-view')?.setAttribute('data-product-url', url.href);
        if (!variant) return;
        this.querySelectorAll('.spf-product__swatch').forEach((swatch) => {
          const selected = data[`option${swatch.dataset.option}`] === swatch.dataset.optionValue;
          swatch.classList.toggle('is-selected', selected);
          swatch.setAttribute('aria-pressed', String(selected));
        });
        const images = [...this.querySelectorAll('.spf-product-card__image')];
        const selectedImage = images.find((img) => (img.dataset.variants || '').split(',').includes(variant.value))
          || images.find((img) => img.dataset.imageId === this.dataset.defaultImage);
        if (selectedImage) {
          images.forEach((img) => {
            img.classList.toggle('spf-product-card__image-main', img === selectedImage);
            img.classList.toggle('spf-product-card__image-hidden', img !== selectedImage);
            img.classList.remove('spf-product-card__image-secondary');
          });
        }
      }
    }
  );
}
